import React from 'react';
import { Users, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';

export const ClientForm = ({
    isOpen, setIsOpen, editingClient, formData, setFormData,
    clientCategories, sendWelcomeEmail, setSendWelcomeEmail,
    showPassword, setShowPassword, onSubmit, onReset
}) => (
    <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) onReset(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-sand">
            <DialogHeader>
                <DialogTitle className="font-heading text-jungle flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-sage/20 flex items-center justify-center">
                        <Users className="w-4 h-4 text-sage" />
                    </div>
                    {editingClient ? 'Modifier le client' : 'Nouveau client'}
                </DialogTitle>
            </DialogHeader>
            <form onSubmit={onSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="client_code" className="text-jungle">Code Client</Label>
                        <Input id="client_code" value={formData.client_code}
                            onChange={(e) => setFormData({ ...formData, client_code: e.target.value.toUpperCase() })}
                            placeholder="Ex: CLI001" className="border-jungle/20 rounded-xl font-mono" />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="client_category" className="text-jungle">Categorie *</Label>
                        <Select value={formData.client_category} onValueChange={(value) => setFormData({ ...formData, client_category: value })}>
                            <SelectTrigger className="border-jungle/20 rounded-xl"><SelectValue placeholder="Selectionner" /></SelectTrigger>
                            <SelectContent className="bg-white z-[100]">
                                {clientCategories.map((cat) => (<SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="company_name" className="text-jungle">Entreprise *</Label>
                        <Input id="company_name" value={formData.company_name}
                            onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                            placeholder="Ex: Franprix Paris 15" className="border-jungle/20 rounded-xl" required />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="siret" className="text-jungle">SIRET</Label>
                        <Input id="siret" value={formData.siret}
                            onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                            placeholder="12345678901234" className="border-jungle/20 rounded-xl" />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="contact_name" className="text-jungle">Nom du contact *</Label>
                        <Input id="contact_name" value={formData.contact_name}
                            onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                            placeholder="Jean Dupont" className="border-jungle/20 rounded-xl" required />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="phone" className="text-jungle">Telephone *</Label>
                        <Input id="phone" value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            placeholder="01 23 45 67 89" className="border-jungle/20 rounded-xl" required />
                    </div>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="email" className="text-jungle">Email *</Label>
                    <Input id="email" type="email" value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="contact@entreprise.fr" className="border-jungle/20 rounded-xl" required disabled={!!editingClient} />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="password" className="text-jungle">
                        Mot de passe {editingClient ? '(laisser vide pour ne pas changer)' : '*'}
                    </Label>
                    <div className="relative">
                        <Input id="password" type={showPassword ? 'text' : 'password'} value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            placeholder="********" className="border-jungle/20 rounded-xl" required={!editingClient} data-testid="admin-client-password" />
                        <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full"
                            onClick={() => setShowPassword(!showPassword)} data-testid="toggle-admin-client-password">
                            {showPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                        </Button>
                    </div>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="address" className="text-jungle">Adresse *</Label>
                    <Input id="address" value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="123 Rue de la Convention, 75015 Paris" className="border-jungle/20 rounded-xl" required />
                </div>
                {!editingClient && (
                    <div className="flex items-center space-x-2 p-3 bg-leaf/10 rounded-xl">
                        <Checkbox id="send_email" checked={sendWelcomeEmail} onCheckedChange={setSendWelcomeEmail} />
                        <label htmlFor="send_email" className="text-sm text-jungle cursor-pointer">
                            Envoyer un email de bienvenue avec les identifiants au client
                        </label>
                    </div>
                )}
                <DialogFooter className="gap-2">
                    <Button type="button" variant="outline" onClick={() => setIsOpen(false)} className="rounded-xl">Annuler</Button>
                    <Button type="submit" className="bg-jungle hover:bg-jungle/90 text-sand rounded-xl">
                        {editingClient ? 'Mettre a jour' : 'Creer le client'}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    </Dialog>
);
